import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component, ChangeDetectionStrategy, Output, EventEmitter, Input, AfterViewInit,
  ViewChild, ElementRef, OnChanges, SimpleChanges, Renderer2, ChangeDetectorRef,
} from '@angular/core';

import { isEmpty } from '@app/util/util';
import { InputTextFailedType, InputTextSize } from '@design-system/components/molecules/input-text/entities/input-text.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';

@Component({
  selector: 'app-ds-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconDsComponent,
    TypographyDsComponent,
  ]
})
export class InputTextDsComponent implements AfterViewInit, OnChanges {

  @Input() size = InputTextSize.RG;
  @Input() failedType = InputTextFailedType.Error;
  @Input() icon: Icon;
  @Input() failedMessage: string;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() value: string;
  @Input() filled = false;
  @Input() staticFilled = false;
  @Input() isReadonly = false;
  @Input() isDisabled = false;
  @Input() autofocus = false;
  @Input() isFailed = false;
  @Input() quickClean = true;
  @Input() highlight = false;
  @Input() maxLength = null;

  @Output() changeEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() clickEvent: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('inputTextDs') inputTextDs: ElementRef<HTMLInputElement>;

  public readonly fontSize = TypographyFontSize.XXS;
  public readonly labelFontSize = TypographyFontSize.XXXS;
  public readonly clearIcon = Icon.Close;
  public readonly iconFillColor = IconFillColor;

  public isFocused = false;

  constructor(
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this.focusOrBlurInputTextListener();
    this.focusOrBlurInputTextSetter(this.autofocus);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.autofocus) {
      this.focusOrBlurInputTextSetter(changes?.autofocus?.currentValue);
    }
  }

  get isFilled(): boolean {
    return this.staticFilled ? this.filled : this.hasValue;
  }

  get fieldsetIsActive(): boolean {
    return this.hasValue && !this.isFailed;
  }

  get failedMessageIsActivated(): boolean {
    return this.isFailed && !isEmpty(this.failedMessage);
  }

  get failedClass(): string {
    return this.isFailed ? this.failedType : InputTextFailedType.None;
  }

  get typographyErrorColor(): string {
    return this.failedClass === InputTextFailedType.Error ? TypographyColor.Error :
      this.failedClass === InputTextFailedType.Warning ? TypographyColor.Warning : TypographyColor.Secondary;
  }

  get hasValue(): boolean {
    return !isEmpty(this.value);
  }

  get quickCleanIsActivated(): boolean {
    return this.quickClean && this.hasValue && !this.isDisabled && !this.isReadonly;
  }

  public valueIsChanged(model: string): void {
    this.changeEvent.emit(model);
  }

  public clean(): void {
    this.value = '';
    this.changeEvent.emit('');
  }

  public onClick(): void {
    if (!this.isDisabled && this.isReadonly) {
      this.clickEvent.emit();
    }
  }

  private focusOrBlurInputTextListener(): void {
    if (this.inputTextDs?.nativeElement) {
      this.renderer.listen(this.inputTextDs?.nativeElement, 'focus', this.setInputTextFocused.bind(this, true));
      this.renderer.listen(this.inputTextDs?.nativeElement, 'blur', this.setInputTextFocused.bind(this, false));
    }
  }

  private focusOrBlurInputTextSetter(state: boolean): void {
    if (state) {
      this.inputTextDs?.nativeElement?.focus();
    } else {
      this.inputTextDs?.nativeElement?.blur();
    }
  }

  private setInputTextFocused(value: boolean): void {
    this.isFocused = value;
    this.cdr.detectChanges();
  }

}
